import { ReactNode } from 'react';

import { Box, Text } from '@opendoor/bricks-next';

import AddressSearch from '../../../shared/AddressSearch';
import { useHeroContext } from './HeroShared';

export interface HeroTitleAndAddressProps {
  title: string;
  subtitle?: string;
  showAddressInput?: boolean;
  renderDesktopInput?: () => ReactNode;
  renderMobileInput?: () => ReactNode;
  additionalAddressContent?: ReactNode;
  addressEntryRef?: (node?: Element | null) => void;
  // Default input props (used when renderDesktopInput/renderMobileInput are not provided)
  addressCtaText?: string;
  addressCtaTextSmall?: string;
  addressCtaPlaceholderText?: string;
  addressCtaPlaceholderTextSmall?: string;
}

// Internal component: HeroHeadline
interface HeroHeadlineProps {
  title: string;
  subtitle?: string;
}

const HeroHeadline = ({ title, subtitle }: HeroHeadlineProps) => {
  return (
    <Box
      flexDirection="column"
      gap="$12x"
      alignItems="center"
      $largerThanMD={{ gap: '$8x', alignItems: 'flex-start' }}
    >
      <Text
        tag="h1"
        typography="$displayXsmall"
        textAlign="center"
        $largerThanSM={{ typography: '$displaySmall' }}
        $largerThanMD={{ typography: '$displayMedium', textAlign: 'left' }}
      >
        {title}
      </Text>
      {subtitle && (
        <Text
          tag="p"
          typography="$bodyMedium"
          textAlign="center"
          $largerThanSM={{ typography: '$bodyLarge' }}
          $largerThanMD={{ textAlign: 'left' }}
        >
          {subtitle}
        </Text>
      )}
    </Box>
  );
};

// Internal component: HeroAddressSection
interface HeroAddressSectionProps {
  showAddressInput?: boolean;
  renderDesktopInput: () => ReactNode;
  renderMobileInput: () => ReactNode;
  additionalContent?: ReactNode;
}

const HeroAddressSection = ({
  showAddressInput,
  renderDesktopInput,
  renderMobileInput,
}: HeroAddressSectionProps) => {
  if (!showAddressInput) return null;

  return (
    <Box
      paddingTop="$16x"
      zIndex={10}
      className="hp-reskin-v2-address-entry-wrapper"
      $largerThanMD={{ paddingTop: '$6x' }}
    >
      <Box id="desktop-address-search" display="none" $largerThanLG={{ display: 'block' }}>
        {renderDesktopInput()}
      </Box>
      <Box id="mobile-address-search" display="block" $largerThanLG={{ display: 'none' }}>
        {renderMobileInput()}
      </Box>
    </Box>
  );
};

export const HeroTitleAndAddress = ({
  title,
  subtitle,
  showAddressInput,
  renderDesktopInput,
  renderMobileInput,
  additionalAddressContent,
  addressEntryRef,
  addressCtaText = 'Get cash offer',
  addressCtaTextSmall = 'Get offer',
  addressCtaPlaceholderText,
  addressCtaPlaceholderTextSmall,
}: HeroTitleAndAddressProps) => {
  const { analyticsPrefix } = useHeroContext();

  const defaultRenderDesktopInput = () => (
    <AddressSearch
      hideLabel
      inputLocation="hero-cta"
      analyticsPrefix={analyticsPrefix}
      trackingTaxonomy="new_homepage"
      actionText={addressCtaText}
      actionTextSmall={addressCtaTextSmall}
      placeholderText={addressCtaPlaceholderText}
      showShadow={false}
    />
  );

  const defaultRenderMobileInput = () => (
    <AddressSearch
      hideLabel
      inputLocation="hero-cta"
      analyticsPrefix={analyticsPrefix}
      trackingTaxonomy="new_homepage"
      actionText={addressCtaText}
      actionTextSmall={addressCtaTextSmall}
      placeholderText={addressCtaPlaceholderTextSmall}
      showShadow={false}
    />
  );

  return (
    <Box id="title-and-address-entry" flexDirection="column" width="100%">
      <HeroHeadline title={title} subtitle={subtitle} />
      <Box display="none" ref={addressEntryRef as never} />
      <HeroAddressSection
        showAddressInput={showAddressInput}
        renderDesktopInput={renderDesktopInput || defaultRenderDesktopInput}
        renderMobileInput={renderMobileInput || defaultRenderMobileInput}
        additionalContent={additionalAddressContent}
      />
    </Box>
  );
};
